import { useEffect, useRef } from 'react';

// Jagged edge points — computed once at module load so every tear uses the same
// hand-ripped silhouette. Values are [yPct, xOffsetPct] pairs.
const EDGE_PTS = (() => {
  const pts = [];
  for (let i = 0; i <= 44; i++) {
    const y = (i / 44) * 100;
    const x =
      Math.abs(Math.sin(i * 2.13 + 0.8)) * 1.9 + Math.abs(Math.cos(i * 3.87 + 1.4)) * 1.1 - 1.6; // centers near 0, varies ± ~1.5%
    pts.push([parseFloat(y.toFixed(2)), parseFloat(x.toFixed(3))]);
  }
  return pts;
})();

function clipAt(cutPct) {
  // Polygon: left edge solid + jagged right edge advancing from 100% → -8%
  let s = '0 0';
  for (const [y, xOff] of EDGE_PTS) {
    s += `,${(cutPct + xOff).toFixed(2)}% ${y}%`;
  }
  s += `,${cutPct.toFixed(2)}% 100%,0 100%`;
  return `polygon(${s})`;
}

function takeSnapshot() {
  const root = document.getElementById('root');
  if (!root) return null;

  const clone = root.cloneNode(true);
  clone.removeAttribute('id');
  clone.style.pointerEvents = 'none';

  // Mirror scrollTop from every scrollable element so the frozen image matches
  // exactly what the user was looking at when they clicked.
  const origEls = root.querySelectorAll('*');
  const cloneEls = clone.querySelectorAll('*');
  origEls.forEach((orig, idx) => {
    const c = cloneEls[idx];
    if (!c || orig.scrollTop === 0) return;
    if (c.firstElementChild) {
      c.style.overflow = 'hidden';
      const prev = c.firstElementChild.style.transform || '';
      c.firstElementChild.style.transform = `${prev} translateY(-${orig.scrollTop}px)`.trim();
    }
  });

  // Kill all CSS transitions and animations in the clone so any in-progress
  // transition (nav active state, button hover, etc.) doesn't continue
  // playing inside the frozen snapshot and cause elements to jump.
  const noTransition = document.createElement('style');
  noTransition.textContent = '* { transition: none !important; animation: none !important; }';
  clone.appendChild(noTransition);

  if (window.scrollY > 0) {
    clone.style.transform = `translateY(-${window.scrollY}px)`;
  }

  const wrap = document.createElement('div');
  // No filter — filter creates a new containing block for position:fixed children,
  // which repositions cloned fixed elements (mobile nav, etc.) unexpectedly.
  // Background provides immediate opaque coverage before the clone paints (fixes mobile flash).
  wrap.style.cssText =
    'position:fixed;inset:0;z-index:99999;overflow:hidden;' +
    'pointer-events:none;background:#f5f0e8';
  wrap.appendChild(clone);
  document.body.appendChild(wrap);
  return wrap;
}

function animateTear(overlay) {
  const duration = 480;
  const start = performance.now();

  function tick(now) {
    const t = Math.min((now - start) / duration, 1);
    // ease-in-out-quad: slow start (paper resistance), fast middle, ease to finish
    const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    overlay.style.clipPath = clipAt(100 - eased * 108); // 100% → -8%

    if (t < 1) {
      requestAnimationFrame(tick);
    } else {
      overlay.remove();
      // Reset scroll so the new page starts at the top regardless of where
      // the previous page was scrolled (iOS scroll restoration can re-apply
      // the old position when the overlay is removed).
      window.scrollTo(0, 0);
      const main = document.getElementById('main-content');
      if (main) main.scrollTop = 0;
    }
  }

  requestAnimationFrame(tick);
}

export default function TearTransition() {
  const active = useRef(false);

  useEffect(() => {
    const noMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // console.error('[TearTransition] effect running | noMotion:', noMotion);
    if (noMotion) return;

    // Intercept history.pushState — fired by React Router on every Link click,
    // after the click handler runs but before React re-renders the new route.
    // This is the ideal moment to snapshot the current page.
    const origPushState = window.history.pushState.bind(window.history);
    // console.error('[TearTransition] pushState patched ✓');

    window.history.pushState = function (...args) {
      // console.error('[TearTransition] pushState called', args[2], '| active:', active.current);
      if (!active.current) {
        active.current = true;
        const overlay = takeSnapshot();
        // console.error('[TearTransition] snapshot taken, overlay:', overlay);
        origPushState(...args);
        if (overlay) {
          requestAnimationFrame(() => {
            // console.error('[TearTransition] animateTear starting');
            animateTear(overlay);
            setTimeout(() => {
              active.current = false;
            }, 550);
          });
        } else {
          active.current = false;
        }
      } else {
        origPushState(...args);
      }
    };

    return () => {
      window.history.pushState = origPushState;
    };
  }, []);

  return null;
}
